import React, { useState } from "react";
import { Typography, Button, Modal, DatePicker, message, Tag } from "antd";
import { CommentOutlined, EditOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { updateTrainingTimeline } from "PlatForms/OrgLMS/Services/MentorServices/TrainingDetailService";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const ProjectOverview = ({ projectData, trainingId, fetchProjects }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [endDate, setEndDate] = useState(projectData?.endDate ? dayjs(projectData.endDate) : null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const projectTimeline =
    projectData?.startDate && projectData?.endDate
      ? `${new Date(projectData.startDate).toLocaleDateString()} - ${new Date(
        projectData.endDate
      ).toLocaleDateString()}`
      : "N/A";

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleDateChange = (date) => {
    setEndDate(date);
  };

  const handleSubmit = async () => {
    if (!endDate) {
      message.error("Please select an end date");
      return;
    }

    try {
      setLoading(true);
      await updateTrainingTimeline(trainingId, endDate.toDate().toISOString());

      message.success("Timeline updated successfully");
      fetchProjects();
      setIsModalVisible(false);
    } catch (error) {
      console.error("Error updating timeline:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGroupConversationClick = () => {
    console.log("Clicked");
    if (projectData?.group?.hasGroupChat && projectData?.group?.conversationId) {
      navigate("/messaging", { state: { selectedChatId: projectData.group.conversationId } });
    }
  };

  // Calculate the min and max allowed dates based on actualEndDate
  const getDateRestrictions = () => {
    if (!projectData?.actualEndDate) return {};

    const completionDate = dayjs(projectData.actualEndDate);
    const minDate = completionDate.startOf("day");
    const maxDate = completionDate.add(7, "day").endOf("day");

    return { minDate, maxDate };
  };

  const { minDate, maxDate } = getDateRestrictions();

  const disabledDate = (current) => {
    if (!projectData?.actualEndDate) {
      return current && current < dayjs().startOf("day");
    }

    return (
      current < minDate.startOf("day") ||
      current > maxDate.endOf("day") ||
      current < dayjs().startOf("day")
    );
  };

  return (
    <>
      <Title level={4} style={{ marginBottom: "10px" }}>
        {projectData?.title || "N/A"}
      </Title>

      <Text style={{ display: "block", marginBottom: "10px" }}>
        <strong>Description:</strong> {projectData?.description || "N/A"}
      </Text>

      <Text style={{ display: "block", marginBottom: "10px" }}>
        <strong>Assigned Group:</strong> {projectData?.groupName || "N/A"}
      </Text>

      <div style={{ display: "flex", alignItems: "center", marginBottom: "10px", gap: 8 }}>
        <Text>
          <strong>Timeline:</strong> {projectTimeline}
        </Text>
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={showModal}
          style={{ padding: "0 4px" }}
        >
          Edit Timeline
        </Button>
      </div>
      <Tag style={{ backgroundColor: "orange", color: "white", marginBottom: "10px" }}>
        {projectData?.isRetryTraining ? "Repeated" : ""}
      </Tag>
      <br />

      <Button type="primary" icon={<CommentOutlined />} onClick={handleGroupConversationClick}>
        {projectData?.group?.groupType === 2 ? "Group Conversation" : "Chat with Participant"}
      </Button>

      <Modal
        title="Edit Training Timeline"
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={loading}
        centered
        okText="Update"
      >
        {projectData?.actualEndDate && (
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ display: "block" }}>
              Current Completion Date:
            </Text>
            <Text>{dayjs(projectData.actualEndDate).format("MMMM D, YYYY")}</Text>
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Select New End Date:
          </Text>
          <DatePicker
            value={endDate}
            onChange={handleDateChange}
            style={{ width: "100%" }}
            disabledDate={disabledDate}
          />
          {projectData?.actualEndDate && (
            <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              Note: Must be between {minDate.format("MMMM D, YYYY")} and{" "}
              {maxDate.format("MMMM D, YYYY")}
            </Text>
          )}
        </div>
      </Modal>
    </>
  );
};

ProjectOverview.propTypes = {
  projectData: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    isRetryTraining: PropTypes.bool,
    groupName: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    actualEndDate: PropTypes.string,
    groupType: PropTypes.number,
    group: PropTypes.shape({
      hasGroupChat: PropTypes.bool,
      conversationId: PropTypes.string,
      groupType: PropTypes.number,
    }),
  }).isRequired,
  trainingId: PropTypes.number.isRequired,
  fetchProjects: PropTypes.func.isRequired,
};

export default ProjectOverview;
